import { getAuthIdToken } from './auth';

export type UserSub = string | null | undefined;

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type HttpError = Error & {
  response?: Response;
  status?: number;
};

interface ApiRequestOptions {
  auth?: boolean;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

const getToken = async () => (await getAuthIdToken()).getJwtToken();

// Normalises a caught value into an HttpError, preserving status/response/message when a
// non-Error object (e.g. { status, response, message }) was thrown instead of an Error instance.
export const toHttpError = (e: unknown): HttpError => {
  if (e instanceof Error) return e as HttpError;

  if (typeof e === 'object' && e !== null) {
    const source = e as { message?: unknown; status?: unknown; response?: unknown };
    const err: HttpError = new Error(typeof source.message === 'string' ? source.message : 'There was a request error');
    if (typeof source.status === 'number') err.status = source.status;
    if (source.response instanceof Response) err.response = source.response;
    return err;
  }

  return new Error(String(e));
};

const apiRequest = async <T = unknown>(
  endpoint: string,
  userSub: UserSub = null,
  method: HttpMethod = 'GET',
  body?: unknown,
  options: ApiRequestOptions = {}
): Promise<T> => {
  const { auth = true, signal, headers: extraHeaders } = options;

  const headers: Record<string, string> = {};

  if (auth) headers.Authorization = await getToken();
  if (userSub) headers['user-sub'] = userSub;
  if (extraHeaders) Object.assign(headers, extraHeaders);

  const params: RequestInit = {
    headers,
    method: method,
  };
  if (signal) params.signal = signal;
  if (method !== 'GET' && !!body) {
    params.body = JSON.stringify(body);
  }

  let res: Response;
  try {
    res = await fetch(endpoint, params);
  } catch (e: unknown) {
    const err = toHttpError(e);

    // Intentional cancellation (e.g. useInstallerBrand aborting a superseded request on cleanup)
    // is not a transport failure - rethrow it as-is rather than logging/relabeling it.
    if (err.name === 'AbortError') {
      throw err;
    }

    // fetch() itself only rejects for network-level failures (offline, DNS, a CORS-blocked
    // response, an aborted request, etc.) - HTTP error statuses resolve normally and are
    // handled below, so only genuine fetch() rejections get the generic friendly message.
    console.error(err);
    const friendlyErr: HttpError = new Error('Unable to reach the server. Please check your connection and try again.');
    friendlyErr.name = err.name;
    throw friendlyErr;
  }

  try {
    if (res.status !== 200) {
      if (!res?.ok) {
        const text = await res.text();
        let resultObject: { message?: string } | undefined;
        try {
          resultObject = JSON.parse(text) as { message?: string };
        } catch {
          // Response is not JSON (e.g., HTML error page)
          const err: HttpError = new Error('Unable to load data. Please try again later.');
          err.response = res;
          err.status = res.status;
          throw err;
        }
        const err: HttpError = new Error(resultObject?.message ?? 'There was a request error');
        err.response = res;
        err.status = res.status;
        throw err;
      }
    }
    const text = await res.text();
    if (!text) {
      return undefined as T;
    }
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as T;
    }
  } catch (e: unknown) {
    // Errors thrown above (HTTP error statuses, unexpected parse/runtime errors) already carry
    // their real status/message - just log and rethrow, without relabeling them as network failures.
    const err: HttpError = toHttpError(e);

    if (err.status !== 404) {
      console.error(err);
    }

    throw err;
  }
};

export default apiRequest;
